import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import useUser from 'sdk/hooks/user/useUser';
import useProfileById from 'sdk/hooks/user/useProfileById';
import useUserFollowersFollowingCount from 'sdk/hooks/follow/useUserFollowersFollowingCount';
import Avatar from 'components/shared/Avatar';
import { ReactComponent as IconBurger } from 'images/burger.svg';
import { ReactComponent as IconUploadPhoto } from 'images/icons/upload_photo.svg';
import { ReactComponent as IconPlay } from 'images/icons/play.svg';
import { ReactComponent as IconMore } from 'images/more.svg';
import Button from 'components/shared/Button';
import useUserIsFollowedByMe from 'sdk/hooks/follow/useUserIsFollowedByMe';
import useFollowUser from 'sdk/hooks/follow/useFollowUser';
import { cloudApi } from 'sdk/api';
import useUserPrivacyActivities from 'sdk/hooks/user/useUserPrivacyActivities';
import useUpdateUser from 'sdk/hooks/user/useUpdateUser';
import useExpertProfile from 'sdk/hooks/expert/useExpertProfile';
import Modal from 'components/shared/modals/Modal';
import { ReactComponent as IconUser } from 'images/user.svg';
import { ReactComponent as IconShield } from 'images/shield-10.svg';
import Video from 'modules/feed/components/Feed/Post/Video';
import AdditionalModal from 'modules/profile/pages/Profile/AdditionalModal';
import { ReactComponent as IconArrowRight } from 'images/arrow-right.svg';
import clsx from 'clsx';
import { useTypedSelector } from 'store/useTypedSelector';
import getCroppedImg from 'utils/cropImage';
import Loader from 'components/shared/Loader/Loader';
import { useActions } from 'store/useActions';
import { states } from 'sdk/utils/states';
import { FollowersModal } from '../FollowersModal/FollowersModal';
import Cropper from 'react-easy-crop';
import { CircleFlag } from 'react-circle-flags';
import useSalonFollowingCount from 'sdk/hooks/follow/useSalonFollowingCount';

import styles from './ProfileTop.module.scss';
import { NoInternetModal } from '../NoInternetModal/NoInternetModal';
import { useTranslation } from 'react-i18next';

enum View {
  Followers,
  Following,
  Assets,
}

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

function ProfileTop() {
    const {t} = useTranslation() as any;
  const userData = useUser();
  const location = useLocation();
  const { id } = useParams();
  const userId = id || userData?.data?.user?.id || '';
  const { data: profile, refetch: refetchProfile } = useProfileById(userId);
  const { data: expertProfile } = useExpertProfile(profile?.expert_id);
  const isOwnProfile =
    Boolean(userData?.data?.user?.id) && (!id || id === userData?.data?.user?.id);
  const navigate = useNavigate();
  const query = useQuery();
  const isFollowedByMeInitially = query.get('followed') === 'true';
  const state = states.find(item => item.code === profile?.state)?.title;
  const { data: count, refetch: refetchCount } = useUserFollowersFollowingCount(userId);
  const { data: isFollowedByMe, refetch: refetchIsFollowedByMe } = useUserIsFollowedByMe(userId);
  const { data: followingSalons, refetch: refetchFollowingSalons } = useSalonFollowingCount(userId);
  const followToUser = useFollowUser(userId);
  const [isOpen, setIsOpen] = useState(false);
  const updateUser = useUpdateUser({ id: userId });

  const [isOpenFullBgVideo, setIsOpenFullBgVideo] = useState(false);
  const [isOpenFullAvatar, setIsOpenFullAvatar] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [, setVideoElement] = useState<HTMLVideoElement | null>(null);
  const [, setVideoPlaying] = useState<boolean>(false);
  const [videoMuted, setVideoMuted] = useState<boolean>(true);
  const [videoProgressHovered, setVideoProgressHovered] = useState<boolean>(false);
  const [isShowAdditionalModal, setIsShowAdditionalModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-shadow
  const { isBecomeOffline } = useTypedSelector(state => state.postSlice);
  const [isOpenInternetModal, setIsOpenInternetModal] = useState(false);

  const [imageModal, setImageModal] = useState(false);
  const [image, setImage] = useState();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const { setIsGetAppModal, setIsSlideChange } = useActions();

  const [isLoadingBg, setIsLoadingBg] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);
 
  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImage: any = await getCroppedImg(image, croppedAreaPixels);

      const response = await cloudApi.uploadMedia(croppedImage);

      await updateUser.mutate(
        {
          avatar: response,
          bg_cover: response,
        },
        {
          onSuccess() {
            refetchProfile();
          },
        },
      );

      setImage(undefined);
      setImageModal(false);
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels]);

  const onAvatarUpload = async () => {
    const avatarForm = document.getElementById('avatarForm') as HTMLFormElement;
    const formData = new FormData(avatarForm);
    setImageModal(true);
    const response = await cloudApi.uploadMedia(formData);

    setImage(response);
  };

  useEffect(() => {
    if (videoRef?.current) {
      const video = videoRef?.current;
      setVideoElement(video);
      video.addEventListener('play', () => {
        setVideoPlaying(true);
      });
      video.addEventListener('pause', () => {
        setVideoPlaying(false);
      });
    }
  }, [videoRef?.current]);

  useEffect(() => {
    const videoEl = videoRef.current;
    return () => {
      if (videoEl) {
        videoEl.pause();
      }
    };
  }, []);

  const handleVideoProgressHover = (isHover: boolean) => {
    setVideoProgressHovered(isHover);
  };

  const handleMuted = value => {
    setVideoMuted(value);
  };

  const { data: settings } = useUserPrivacyActivities(userId);
  const [view, setView] = useState<View>(View.Followers);

  const onClose = () => {
    setIsOpen(false);
  };

  const onOpen = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    onClose();
  }, [id]);

  useEffect(() => {
    const refetchData = async () => {
      await refetchCount();
      await refetchProfile();
      await refetchFollowingSalons();
    };

    refetchData();
  }, []);

  const [isSignUpVisible, setSignUpVisible] = useState<boolean>(false);
  const [isSignInVisible, setSignInVisible] = useState<boolean>(false);
  const [signInIntitialStep, setSignInInitialStep] = useState<string>('');
  const [passResetVisiblePage, setPassResetVisiblePage] = useState<string>('');
  const { data: currentUser } = useUser();

  const onFollow = async () => {
    if (window.innerWidth < 768) {
      setIsGetAppModal(Boolean(window.innerWidth < 768));
      return;
    }
    if (isBecomeOffline) {
      setIsOpenInternetModal(true);
      return;
    }
    if (!currentUser) {
      setSignUpVisible(true);
    }
    followToUser.mutate(true, {
      onSuccess() {
        refetchCount();
        refetchIsFollowedByMe();
      },
    });
  };

  const onUnFollow = async () => {
    
    if (window.innerWidth < 768) {
      setIsGetAppModal(Boolean(window.innerWidth < 768));
      return;
    }
    if (isBecomeOffline) {
      setIsOpenInternetModal(true);
      return;
    }
    followToUser.mutate(false, {
      onSuccess() {
        refetchCount();
        refetchIsFollowedByMe();
      },
    });
  };

  const onFollowersClick = () => {
    if (!currentUser) {
      setSignUpVisible(true);
      return;
    }
    onOpen();
    setView(View.Followers);
  };

  const onFollowingsClick = () => {
    if (!currentUser) {
      setSignUpVisible(true);
      return;
    }
    onOpen();
    if (settings?.following !== false || isOwnProfile) {
      setView(View.Following);
    }
  };

  const followBtn = () => {
 
    if (isOwnProfile) {
      return '';
    }

    if (isFollowedByMe ?? isFollowedByMeInitially) {
      return (
        <Button
          className={styles.unFollowBtn}
          onClick={onUnFollow}
          disabled={followToUser.isLoading}
          green
        >
         {t('Following')}
        </Button>
      );
    }

    return (
      <Button
        className={styles.followBtn}
        onClick={onFollow}
        disabled={followToUser.isLoading}
        success
      >
        {t('Follow')}
      </Button>
    );
  };

  const onBgUpload = async () => { 
    setIsLoadingBg(true);
    const bgForm = document.getElementById('bgForm') as HTMLFormElement;
    const formData = new FormData(bgForm);
    const response = await cloudApi.uploadMedia(formData);
    setIsLoadingBg(false);
    updateUser.mutate(
      { bg_cover: response },
      {
        onSuccess() {
          refetchProfile();
        },
      },
    );
  };

  const getMedia = (link: string) => {
    
    if (!link) {
      return '';
    }

    if (link.slice(-3) === 'mp4' || link.slice(-3) === 'mov' || link.slice(-3) === '3gp') {
      return <video src={link} muted autoPlay loop />;
    }

    return <img src={link} alt={profile?.username} />;
  };

  const getCountryName = (code: string) => {

    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

    if (code.length === 2) {
      return regionNames.of(code);
    }
    return code;
  };
 
  return (
    <>
      <FollowersModal
        isOpen={isOpen}
        onClose={onClose}
        userId={userId}
        profileId={userData?.data?.user?.id}
        isOwnProfile={isOwnProfile}
        view={view}
        setView={setView}
      />
      <div className={styles.container}>
        <>
          <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
            <IconArrowRight />
          </button>
          <div className={styles.topRightMenu}>
            {isOwnProfile ? (
              <Link to="/profile-menu" className={styles.burgerBtn}>
                <IconBurger />
              </Link>
            ) : null}
            {isOwnProfile ? (
              <div className={styles.dotsBtn} onClick={() => setIsShowAdditionalModal(true)}>
                <IconMore />
              </div>
            ) : null}
          </div>
          <div
            className={clsx(
              styles.profile,
              updateUser.isLoading || isLoadingBg ? 'blockLoading' : '',
            )}
          >
            <div className={styles.profileImg}>
              {!profile?.expert_id
                ? getMedia(profile?.bg_cover)
                : getMedia(expertProfile?.bg_video)}
            </div>
            {/* <div className={styles.username} style={{ marginBottom: (!profile?.bg_cover && !expertProfile?.bg_video && !isOwnProfile) ? '77px' : '' }}>
                  {profile?.username ? `@${profile?.username}` : null}
                </div> */}
            <div className={styles.avatarRow}>
              {/* <div className={styles.followers} onClick={onFollowersClick}>
                    {
                      count?.followers ? (
                        <div className={styles.followersValue}>{count?.followers || '0'}</div>
                      ) : <div className={styles.followersValue} />
                    }
                    {
                      count?.followers || profile?.id === currentUser?.user.id ? (
                        <div className={styles.followersTitle}>Followers</div>
                      ) : <div className={styles.followersValue} />
                    }

                  </div> */}
              {expertProfile?.bg_video && (
                <IconPlay className={styles.playIcon} onClick={() => setIsOpenFullBgVideo(true)} />
              )}

              <Modal
                onClose={() => setImageModal(false)}
                isOpen={imageModal}
                onClick={(e: any) => e.stopPropagation()}
                className={styles.cropper}
              >
                <div className={styles.cropContainer}>
                  {image ? (
                    <Cropper
                      image={image}
                      crop={crop}
                      onCropChange={setCrop}
                      zoom={zoom}
                      aspect={4 / 4}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      restrictPosition
                    />
                  ) : (
                    <Loader className={styles.cropLoader} />
                  )}
                </div>

                <Button wide onClick={showCroppedImage}>
                  {t('Apply')}
                </Button>
              </Modal>
              {isOwnProfile && !profile?.avatar ? (
                <form id="avatarForm" className={styles.avatar}>
                  <label htmlFor="avatarUpload">
                    <input
                      type="file"
                      name="file"
                      id="avatarUpload"
                      className={styles.bgUploadInput}
                      onChange={onAvatarUpload}
                      accept="image/*"
                    />
                    <IconUploadPhoto />
                  </label>
                </form>
              ) : (
                <Avatar
                  className={styles.avatar}
                  image={profile?.avatar}
                  isExpert={profile?.expert_id}
                  onClick={() => (profile?.avatar ? setIsOpenFullAvatar(true) : null)}
                />
              )}
              <div className={styles.followers} onClick={() => navigate('/profile/following')}>
                {followingSalons !== undefined ? (
                  <div className={styles.followersValue}>
                    {typeof followingSalons === 'number' ? String(followingSalons) : '0'}
                  </div>
                ) : (
                  <div className={styles.followersValue} />
                )}
                {followingSalons !== undefined || profile?.id === currentUser?.user.id ? (
                  <div className={styles.followersTitle}>{t("Following")}</div>
                ) : (
                  <div className={styles.followersValue} />
                )}
              </div>
            </div>
            <div className={styles.location}>
              {profile?.country && settings?.location !== false ? (
                <div className={styles.countryRow}>
                  <CircleFlag
                    countryCode={profile?.country?.toLowerCase() || ''}
                    height="20"
                    className={styles.flag}
                  />
                  <span className={styles.countryName}>
                    {profile?.country ? getCountryName(profile?.country || 'US') : ''}
                    {state && profile.country === 'US' && `, ${state}`}
                  </span>
                </div>
              ) : (
                ''
              )}
            </div>
            <div className={styles.name}>{profile?.username || ''}</div>
            <Button
              white
              small
              className={styles.bookButton}
              onClick={() => navigate('/profile/edit')}
            >
              {t('Edit Profile')}
            </Button>
            <div />
            {followBtn()}
          </div>
        </>
      </div>
      <Modal
        onClose={() => setIsOpenFullBgVideo(false)}
        isOpen={isOpenFullBgVideo}
        onClick={(e: any) => e.stopPropagation()}
        className={styles.fullBgVideoModal}
      >
        <Video
          src={expertProfile?.bg_video}
          handleVideoMuted={handleMuted}
          isActiveSlide
          onProgressHover={handleVideoProgressHover}
          progressHovered={videoProgressHovered}
          className={styles.video}
        />
        <div className={styles.userRow}>
          <span className={styles.avatarContainer}>
            {profile?.avatar ? (
              <img src={profile?.avatar} alt={profile?.username} />
            ) : (
              <IconUser className={styles.avatarDefault} />
            )}
          </span>
          <div className={styles.usernameCol}>
            <span>{`${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || userData?.data?.user?.user_metadata?.full_name || profile?.username || ''}</span>
            <span className={styles.username}>
              @{profile?.username}
              <IconShield />
            </span>
          </div>
        </div>
      </Modal>
      <Modal
        onClose={() => setIsOpenFullAvatar(false)}
        isOpen={isOpenFullAvatar}
        onClick={(e: any) => e.stopPropagation()}
        className={clsx(styles.fullBgVideoModal, styles.fullBgImageModal)}
      >
        <img src={profile?.avatar} alt={profile?.avatar} />
      </Modal>
      <AdditionalModal
        isOpen={isShowAdditionalModal}
        onClose={() => setIsShowAdditionalModal(false)}
        setIsShowAdditionalModal={setIsShowAdditionalModal}
      />
      <NoInternetModal onClose={() => setIsOpenInternetModal(false)} isOpen={isOpenInternetModal} />
    </>
  );
}

export default ProfileTop;
